import type { ReactNode } from 'react'
import { useReducer, useRef } from 'react'

import { Code, ConnectError } from '@connectrpc/connect'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Flex,
  PrimaryButton,
  Spinner,
  Text,
  TextButton,
} from '@enechain/ecloud-designsystem'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import {
  CsvUploadStep1Panel,
  type CsvUploadStep1PanelProps,
} from '~/components/common/CsvUpload/CsvUploadStep1Panel'
import { Stepper } from '~/components/common/CsvUpload/Stepper'
import { DropZone } from '~/components/upload/DropZone'
import { now as serverNow } from '~/models/clock'
import type { AcceptedCsvHeaders } from '~/models/csvAcceptedHeaders'
import { formatJstTimeHm } from '~/models/dateTime'

/**
 * `onCheck` の戻り値契約。データ種別ごとの Check レスポンスは形が異なるが、
 * 共通ウィザードが必要とするのは check_token だけなので、これだけを
 * 共通契約として要求する (design doc §6「components/upload/」の共通化方針)。
 */
type CheckResultBase = { checkToken: string }

/**
 * 選択済みファイルのメタ情報 (名前・サイズ・選択時刻)。Step2 と確定モーダルの
 * 上部に常時表示する (誤ファイル防止、common.md AC-DUC-005/006)。File 自体が name/size を持つので
 * selectedAt (選択時刻) だけを別途持ち回る。
 */
type SelectedFile = { file: File; selectedAt: Date }

/**
 * 確定の失敗。 `outcomeUnknown` は結果が返らなかった失敗 (`awaitsApplyResult` の data type
 * だけが作る)、 `reported` は文言を出す失敗 (それ以外の失敗。 既定の data type は、 通信エラー
 * などを含む全失敗がここに入る)。
 */
type ApplyFailure =
  | { kind: 'outcomeUnknown' }
  | {
      kind: 'reported'
      message: string
      /** 確定時の再検証による取込全体の拒否 (FailedPrecondition) の error。 拒否でなければ undefined。 */
      rejection: ConnectError | undefined
    }

type WizardState<TCheckResult extends CheckResultBase> =
  | { phase: 'idle' }
  | { phase: 'checkRejected'; message: string }
  | ({ phase: 'checking'; checkId: number } & SelectedFile)
  | ({ phase: 'checkError'; message: string } & SelectedFile)
  | ({ phase: 'checked'; result: TCheckResult } & SelectedFile)
  | ({ phase: 'confirming'; result: TCheckResult } & SelectedFile)
  | ({ phase: 'applying'; result: TCheckResult } & SelectedFile)
  | ({
      phase: 'applyError'
      result: TCheckResult
      failure: ApplyFailure
    } & SelectedFile)
  | ({ phase: 'tokenExpired' } & SelectedFile)
  | ({ phase: 'applyConflict' } & SelectedFile)

type Action<TCheckResult extends CheckResultBase> =
  | ({ type: 'CHECK_START'; checkId: number } & SelectedFile)
  | { type: 'CHECK_SUCCESS'; checkId: number; result: TCheckResult }
  | { type: 'CHECK_FAILURE'; checkId: number; message: string }
  | { type: 'CHECK_REJECTED'; checkId: number; message: string }
  | { type: 'PROCEED_TO_CONFIRM' }
  | { type: 'CANCEL_CONFIRM' }
  | { type: 'BACK_TO_STEP1' }
  | { type: 'APPLY_START' }
  | { type: 'APPLY_SUCCESS' }
  | { type: 'APPLY_TOKEN_EXPIRED' }
  | { type: 'APPLY_CONFLICT' }
  | { type: 'APPLY_FAILURE'; failure: ApplyFailure }

/**
 * 確定の結果が返らなかった失敗の code (common.md AC-DUC-041 の通信エラー・システムエラー)。
 * これ以外の code は、 サーバーが理由を返した失敗 (拒否・競合・権限など) として、 その理由を出す。
 */
const APPLY_OUTCOME_UNKNOWN_CODES: ReadonlySet<Code> = new Set([
  Code.Canceled,
  Code.Unknown,
  Code.DeadlineExceeded,
  Code.Internal,
  Code.Unavailable,
  Code.DataLoss,
])

function isApplyOutcomeUnknown(error: unknown): boolean {
  return (
    !(error instanceof ConnectError) ||
    APPLY_OUTCOME_UNKNOWN_CODES.has(error.code)
  )
}

function reducer<TCheckResult extends CheckResultBase>(
  state: WizardState<TCheckResult>,
  action: Action<TCheckResult>,
): WizardState<TCheckResult> {
  switch (action.type) {
    case 'CHECK_START':
      return {
        phase: 'checking',
        checkId: action.checkId,
        file: action.file,
        selectedAt: action.selectedAt,
      }
    // Check の結果は、 いま待っている要求 (checkId) のものだけ受け取る。 phase
    // だけで受けると、 確認中に Step1 へ戻って別ファイルを選んだ後に先行ファイル
    // の結果が届いた時、 新しいファイルの表示に古い checkToken が紐付き、
    // 確定で古いファイルを Apply してしまう。
    case 'CHECK_SUCCESS':
      return state.phase === 'checking' && state.checkId === action.checkId
        ? {
            phase: 'checked',
            file: state.file,
            selectedAt: state.selectedAt,
            result: action.result,
          }
        : state
    case 'CHECK_FAILURE':
      return state.phase === 'checking' && state.checkId === action.checkId
        ? {
            phase: 'checkError',
            file: state.file,
            selectedAt: state.selectedAt,
            message: action.message,
          }
        : state
    case 'CHECK_REJECTED':
      return state.phase === 'checking' && state.checkId === action.checkId
        ? { phase: 'checkRejected', message: action.message }
        : state
    case 'PROCEED_TO_CONFIRM':
      return state.phase === 'checked'
        ? {
            phase: 'confirming',
            file: state.file,
            selectedAt: state.selectedAt,
            result: state.result,
          }
        : state
    // 確定モーダルを閉じて Step2 (checked) に戻る。 applying 中は受理しない —
    // ConfirmDialog は isLoading 中も Cancel が押せ、 Modal は Esc / × でも
    // onCancel を呼ぶ。 ここで閉じると APPLY_FAILURE (applying でしか反映されない)
    // が失われ、 token 消費済みの確定を checked から再送できてしまう。
    case 'CANCEL_CONFIRM':
      return state.phase === 'confirming' || state.phase === 'applyError'
        ? {
            phase: 'checked',
            file: state.file,
            selectedAt: state.selectedAt,
            result: state.result,
          }
        : state
    case 'BACK_TO_STEP1':
      return { phase: 'idle' }
    case 'APPLY_START':
      return state.phase === 'confirming' || state.phase === 'applyError'
        ? {
            phase: 'applying',
            file: state.file,
            selectedAt: state.selectedAt,
            result: state.result,
          }
        : state
    case 'APPLY_SUCCESS':
      return { phase: 'idle' }
    // 確定の終端も Step2 なので、 選択ファイルの情報を持ち続ける
    // (common.md AC-DUC-005: Step2 の上部に常時表示)。
    case 'APPLY_TOKEN_EXPIRED':
      return state.phase === 'applying'
        ? {
            phase: 'tokenExpired',
            file: state.file,
            selectedAt: state.selectedAt,
          }
        : state
    case 'APPLY_CONFLICT':
      return state.phase === 'applying'
        ? {
            phase: 'applyConflict',
            file: state.file,
            selectedAt: state.selectedAt,
          }
        : state
    case 'APPLY_FAILURE':
      return state.phase === 'applying'
        ? {
            phase: 'applyError',
            file: state.file,
            selectedAt: state.selectedAt,
            result: state.result,
            failure: action.failure,
          }
        : state
    default:
      return state
  }
}

function formatFileSizeKB(bytes: number): string {
  return `${Math.round(bytes / 1024).toLocaleString()} KB`
}

/**
 * `idle` phase (Step1) は `CsvUploadStep1Panel` をそのまま描く closed な
 * 差し込み口 (Design Note `upload-wizard-step1-panel.md` D1/D3)。
 * `downloads` / `selectHint` / `sampleRow` / `guideSections` は素通しで
 * `CsvUploadStep1Panel` へ渡るだけなので、意味は同 component の
 * `CsvUploadStep1PanelProps` (単一 SoT) に譲り、ここでは再宣言しない
 * (`children` だけは wizard 自身が DropZone で埋めるため除く)。
 */
type Props<TCheckResult extends CheckResultBase, TApplyResult> = Omit<
  CsvUploadStep1PanelProps,
  'children'
> & {
  /** Step1 のガイダンスとヘッダー不一致エラーで示す列名 (DropZone にそのまま渡す)。 */
  expectedHeader: string[]
  /** 受理するヘッダー (DropZone にそのまま渡す。 照合ルールは `AcceptedCsvHeaders`)。 */
  acceptedHeaders: AcceptedCsvHeaders
  /** ガイダンスとヘッダー不一致エラーで示す列名 (DropZone にそのまま渡す)。 */
  displayHeader?: string[]
  /** ファイルサイズ上限 (bytes)。 */
  maxFileSizeBytes: number
  /** データ行数上限 (DropZone のガイダンス表示用)。 未指定 = 行数上限なし。 */
  maxRowCount?: number
  /**
   * カラム名行の 0 起算インデックス (DropZone にそのまま渡す)。 既定 0 =
   * 1 行ヘッダー、 1 = 2 行ヘッダー (0 行目が説明テキスト)。
   * `admin/data/uploads/common.md` §1「ヘッダー行の構造」参照。
   */
  headerRowIndex?: number
  /** CSV を Check する (harmless)。 */
  onCheck: (file: File) => Promise<TCheckResult>
  /** check_token を Apply する (実登録)。 */
  onApply: (checkToken: string) => Promise<TApplyResult>
  /** Step2 の件数サマリ・差分プレビュー表示 (データ種別ごとの文言)。 */
  renderSummary: (result: TCheckResult) => ReactNode
  /**
   * 確定モーダルのうち data type ごとに異なる部分 (design `#btn-open-confirm`
   * のラベル / モーダルタイトル / 実行ボタン `#btn-commit` / 案内文 + 件数)。
   * モーダル上部のファイル情報 (common.md AC-DUC-005) と Apply 失敗の表示
   * (AC-DUC-023) は全 data type 共通なので wizard が描く。 `renderBody` の件数は
   * Check レスポンスにある値だけを出す (AC-DUC-020: Step2 と永続化内容の一致)。
   */
  confirmDialog: {
    openLabel: string
    title: string
    confirmLabel: string
    renderBody: (result: TCheckResult) => ReactNode
  }
  /** 確定成功時に呼ばれる (caller が完了トースト等を表示する)。 */
  onComplete: (result: TApplyResult) => void
  /**
   * true の間は Step1 でファイルを受け付けない。 確定成功で Step1 に戻った後、 caller が
   * 別の画面へ移るのを待つ間に次の取込を始めさせないために使う。
   */
  fileSelectionDisabled?: boolean
  /**
   * Step2 から確定へ進めるか。 `admin/data/uploads/common.md` §4 の
   * 「業務バリデーションエラー: 該当行にエラー表示、 確定モーダルへの進行不可」
   * を、 データ種別ごとのエラー判定 (Check レスポンスの形が種別で違う) を
   * 注入して実現する口。 未指定なら常に進める (既存呼び出し元の挙動を変えない)。
   */
  canProceed?: (result: TCheckResult) => boolean
  /**
   * 確定時の再検証で取込全体を拒否された (FailedPrecondition) とき、 確定モーダルを
   * エラー表示 (確定ボタンなし。 操作は既定の画面が「閉じる」、 `awaitsApplyResult` の
   * 画面が「ファイル選択に戻る」のみ) に切り替えてその場に出す内容。
   * 拒否理由のメッセージと、 拒否の error (error detail で運ばれる構造を読むため) を
   * 受け取り、 モーダル本文の ReactNode を返す。 undefined を
   * 返せば通常の Alert 表示に落とす。 未指定なら常に通常の Alert (既存呼び出し元の
   * 挙動を変えない)。 入札 CSV の確定時エラー (Des 回答 #8786) が使う。
   */
  renderApplyRejection?: (
    message: string,
    error: ConnectError,
  ) => ReactNode | undefined
  /**
   * 確定の結果を受け取るまで待つ画面 (入札 CSV。 common.md AC-DUC-039〜041)。
   * 共通仕様の非同期の確定 (AC-DUC-017〜023) の例外で、 true の時:
   * - 待つ間 (applying) は、 実行ボタンのローディング表示に加えて画面を閉じないよう
   *   案内する。 結果を受け取るまで確定モーダルは閉じられない (CANCEL_CONFIRM は
   *   applying を受理しない。 AC-DUC-039)。
   * - 確定の失敗は、 どの失敗でもモーダル内で確定し直させず (AC-DUC-023 の再試行を適用しない)、
   *   実行ボタンと確定の本文を出さない。 確定モーダルを閉じると Step1 に戻る。
   *   - 取込全体の拒否 (FailedPrecondition): token が消費済みなので、 拒否の内容を出し、
   *     モーダルの操作は「ファイル選択に戻る」だけにする (AC-DUC-040)。
   *   - 結果が返らなかった失敗 (通信エラー・システムエラー): 結果が分からないまま再実行すると
   *     同じ札が二重になりうるため、 アップロード履歴で結果を確認するよう案内する (AC-DUC-041)。
   *   - それ以外 (サーバーが理由を返した失敗): その理由を出す。
   *
   * 前提: `onApply` は、 失敗を Connect の code を保った `ConnectError` で reject する (文言を
   * 訳して作り直す時も code を変えない)。 結果が返らなかった失敗かどうかを code で見分けるため。
   *
   * 未指定なら従来どおり (取込全体の拒否の表示は閉じると Step2 に戻って同じ token で確定を
   * やり直せ、 それ以外の失敗はモーダル内に出して実行ボタンで再試行できる。 AC-DUC-023)。
   */
  awaitsApplyResult?: boolean
}

/**
 * CSV アップロードの 2 ステップウィザード共通部品 (common.md §1: Step1 = ファイルの
 * 準備・選択、Step2 = 差分プレビューして完了 + Step2 内から起動する確定モーダル)。
 * データ種別ごとの差分 (Check/Apply RPC、件数サマリ・モーダル本文の文言、完了時の
 * 副作用) は props で注入され、本体は phase 遷移 + エラー分岐の骨格を持つ。
 *
 * check_token の NotFound (5) は「有効期限が切れました」誘導 (AC-04)、
 * AlreadyExists (6) は「既に登録されている供給地点が含まれています」誘導
 * (design doc §7 重点ケース「排他防波堤」) として、それぞれハードコードで
 * 解釈する — Connect の Code は RPC 横断の安定した契約であり、データ種別
 * ごとに変わるものではないため props 化しない。
 *
 * AlreadyExists は「再試行すれば通る」種類のエラーではない: 本 slice は所属
 * 行を常に INSERT するため、登録済みの供給地点を含む CSV は何度出し直しても
 * 同じ結果になる (差分 upsert = 据え置き / 変更 は slice 2/3)。そのため文言は
 * 「やり直してください」ではなく CSV 側で何を直すかを示す。
 */
export const UploadWizard = <
  TCheckResult extends CheckResultBase,
  TApplyResult,
>({
  expectedHeader,
  acceptedHeaders,
  displayHeader,
  maxFileSizeBytes,
  maxRowCount,
  headerRowIndex,
  onCheck,
  onApply,
  renderSummary,
  onComplete,
  fileSelectionDisabled = false,
  canProceed,
  confirmDialog,
  renderApplyRejection,
  awaitsApplyResult = false,
  downloads,
  selectHint,
  sampleRow,
  guideSections,
}: Props<TCheckResult, TApplyResult>): React.ReactElement => {
  const { t } = useTranslation()
  const [state, dispatch] = useReducer(reducer<TCheckResult>, {
    phase: 'idle',
  } as WizardState<TCheckResult>)

  // 「チェック通信エラー: エラー + 再試行ボタン / 再試行可 / ファイル
  // 再選択不要」(設計書 画面状態マトリクス) — 保持済みファイルに対して
  // onCheck を再実行する。ファイル選択時の初回実行と再試行の両方から
  // 呼ばれる単一の入口にすることで、「retry が checking 状態に遷移する
  // だけで再実行されない」という抜けを構造的に防ぐ。
  const nextCheckIdRef = useRef(0)
  const runCheck = (file: File, selectedAt: Date): void => {
    nextCheckIdRef.current += 1
    const checkId = nextCheckIdRef.current
    dispatch({ type: 'CHECK_START', checkId, file, selectedAt })
    onCheck(file)
      .then((result) => dispatch({ type: 'CHECK_SUCCESS', checkId, result }))
      .catch((error: unknown) => {
        const message =
          error instanceof ConnectError
            ? error.rawMessage
            : t('common:upload.error.generic')
        const type =
          error instanceof ConnectError &&
          error.code === Code.FailedPrecondition
            ? 'CHECK_REJECTED'
            : 'CHECK_FAILURE'
        dispatch({ type, checkId, message })
      })
  }

  const runApply = (checkToken: string): void => {
    dispatch({ type: 'APPLY_START' })
    onApply(checkToken)
      .then((result) => {
        dispatch({ type: 'APPLY_SUCCESS' })
        onComplete(result)
      })
      .catch((error: unknown) => {
        if (error instanceof ConnectError && error.code === Code.NotFound) {
          dispatch({ type: 'APPLY_TOKEN_EXPIRED' })
          return
        }
        if (
          error instanceof ConnectError &&
          error.code === Code.AlreadyExists
        ) {
          dispatch({ type: 'APPLY_CONFLICT' })
          return
        }
        if (awaitsApplyResult && isApplyOutcomeUnknown(error)) {
          dispatch({
            type: 'APPLY_FAILURE',
            failure: { kind: 'outcomeUnknown' },
          })
          return
        }
        dispatch({
          type: 'APPLY_FAILURE',
          failure: {
            kind: 'reported',
            message:
              error instanceof ConnectError
                ? error.rawMessage
                : t('common:upload.error.generic'),
            rejection:
              error instanceof ConnectError &&
              error.code === Code.FailedPrecondition
                ? error
                : undefined,
          },
        })
      })
  }

  const renderFileInfo = (file: File, selectedAt: Date): ReactNode => (
    <Flex alignItems="baseline" gap={2}>
      <Text fontWeight="medium">{file.name}</Text>
      <Text color="Text.midEmphasis" fontSize="sm">
        {t('common:upload.fileInfo.meta', {
          size: formatFileSizeKB(file.size),
          time: formatJstTimeHm(selectedAt),
        })}
      </Text>
    </Flex>
  )

  const backToStep1Button = (
    <TextButton onClick={() => dispatch({ type: 'BACK_TO_STEP1' })}>
      {t('common:upload.action.backToStep1')}
    </TextButton>
  )

  /**
   * 確定の失敗の表示。 `rejected` は、 取込全体の拒否として `renderApplyRejection` が内容を
   * 返したか。
   */
  const renderApplyFailure = (
    failure: ApplyFailure,
  ): { content: ReactNode; rejected: boolean } => {
    if (failure.kind === 'outcomeUnknown') {
      return {
        content: (
          <Alert
            status="error"
            title={t('common:upload.error.applyResultUnknown')}
          />
        ),
        rejected: false,
      }
    }
    const rejection =
      failure.rejection === undefined
        ? undefined
        : renderApplyRejection?.(failure.message, failure.rejection)
    return rejection === undefined
      ? {
          content: <Alert status="error" title={failure.message} />,
          rejected: false,
        }
      : { content: rejection, rejected: true }
  }

  const renderPhase = (): ReactNode => {
    // idle = Step1 (Design Note upload-wizard-step1-panel.md D1)。 Step1 パネル
    // 全体 (雛形 DL・記入例案内・記入ガイド表・選択案内・DropZone) の節構成は
    // `CsvUploadStep1Panel` に閉じ、 wizard は DropZone を children として置く
    // だけの closed 型の差し込み口にする (D3)。
    if (state.phase === 'idle' || state.phase === 'checkRejected') {
      return (
        <CsvUploadStep1Panel
          downloads={downloads}
          guideSections={guideSections}
          sampleRow={sampleRow}
          selectHint={selectHint}
        >
          {state.phase === 'checkRejected' && (
            <Alert status="error" title={state.message} />
          )}
          <DropZone
            acceptedHeaders={acceptedHeaders}
            disabled={fileSelectionDisabled}
            displayHeader={displayHeader}
            expectedHeader={expectedHeader}
            headerRowIndex={headerRowIndex}
            maxFileSizeBytes={maxFileSizeBytes}
            maxRowCount={maxRowCount}
            onFileAccepted={(file) => runCheck(file, serverNow())}
          />
        </CsvUploadStep1Panel>
      )
    }

    // token 消費済みで再試行しても通らない終端は、 モーダルを閉じて Step2 の
    // Alert + 戻る導線にする (AC-DUC-023 の「再試行可能」の対象外)。
    if (state.phase === 'tokenExpired') {
      return (
        <Flex flexDirection="column" gap={3}>
          {renderFileInfo(state.file, state.selectedAt)}
          <Alert status="error" title={t('common:upload.error.tokenExpired')} />
          {backToStep1Button}
        </Flex>
      )
    }

    if (state.phase === 'applyConflict') {
      return (
        <Flex flexDirection="column" gap={3}>
          {renderFileInfo(state.file, state.selectedAt)}
          <Alert status="error" title={t('common:upload.error.conflict')} />
          {backToStep1Button}
        </Flex>
      )
    }

    if (state.phase === 'checking') {
      return (
        <Flex flexDirection="column" gap={3}>
          {renderFileInfo(state.file, state.selectedAt)}
          <Flex alignItems="center" gap={2}>
            <Spinner />
            <Text>{t('common:upload.status.checking')}</Text>
          </Flex>
          {/* AC-DUC-034: Step2 からはいつでも Step1 に戻れる。 戻った後に届いた
              Check の結果は reducer が checking 以外では受け取らないので捨てられる。 */}
          {backToStep1Button}
        </Flex>
      )
    }

    if (state.phase === 'checkError') {
      return (
        <Flex flexDirection="column" gap={3}>
          {renderFileInfo(state.file, state.selectedAt)}
          <Alert status="error" title={state.message} />
          <Flex gap={2}>
            <TextButton onClick={() => runCheck(state.file, state.selectedAt)}>
              {t('common:upload.action.retry')}
            </TextButton>
            {backToStep1Button}
          </Flex>
        </Flex>
      )
    }

    // checked / confirming / applying / applyError = Step2。 confirming 以降は
    // この表示の上に確定モーダルが開く (common.md「確定モーダル（Step2 内から
    // 起動）」)。
    return (
      <Flex flexDirection="column" gap={3}>
        {renderFileInfo(state.file, state.selectedAt)}
        {renderSummary(state.result)}
        <Flex gap={2}>
          {backToStep1Button}
          <PrimaryButton
            disabled={canProceed !== undefined && !canProceed(state.result)}
            onClick={() => dispatch({ type: 'PROCEED_TO_CONFIRM' })}
            size="sm"
          >
            {confirmDialog.openLabel}
          </PrimaryButton>
        </Flex>
      </Flex>
    )
  }

  const dialogState =
    state.phase === 'confirming' ||
    state.phase === 'applying' ||
    state.phase === 'applyError'
      ? state
      : undefined

  const applyFailure =
    dialogState?.phase === 'applyError' ? dialogState.failure : undefined
  const failureView =
    applyFailure === undefined ? undefined : renderApplyFailure(applyFailure)
  // 確定し直せない失敗か。 結果を待つ画面 (awaitsApplyResult) はどの失敗でも (common.md
  // AC-DUC-040 / AC-DUC-041)、 既定の画面は取込全体の拒否の表示だけ (再実行しても同じ結果に
  // なる)。 確定の本文と実行ボタンを出さない。
  const failureIsFinal =
    failureView !== undefined && (awaitsApplyResult || failureView.rejected)
  // 結果を待つ画面で確定し直せない失敗は、 token が消費済み (拒否) か結果が分からない
  // (返らなかった) ので、 Step2 に戻して同じ token で実行ボタンを押せる状態にせず、
  // Step1 からやり直す (AC-DUC-040・041)。
  const closeBacksToStep1 = awaitsApplyResult && failureIsFinal
  // AC-DUC-040: 取込全体の拒否 (FailedPrecondition) は「ファイル選択に戻る」で Step1 からの
  // やり直しを案内する。 それ以外の確定し直せない失敗は「閉じる」(AC-DUC-041)。
  const applyRejected =
    applyFailure?.kind === 'reported' && applyFailure.rejection !== undefined

  const resolveCancelLabel = (): string | undefined => {
    if (!failureIsFinal) return undefined
    return closeBacksToStep1 && applyRejected
      ? t('common:upload.action.backToStep1')
      : t('common:action.close')
  }

  return (
    <Flex flexDirection="column" gap={4}>
      <Stepper
        currentStep={
          state.phase === 'idle' || state.phase === 'checkRejected' ? 1 : 2
        }
        stepLabels={[
          t('common:upload.stepper.step1'),
          t('common:upload.stepper.step2'),
        ]}
      />
      {renderPhase()}
      {dialogState !== undefined && (
        <ConfirmDialog
          body={
            <Flex flexDirection="column" gap={3}>
              {renderFileInfo(dialogState.file, dialogState.selectedAt)}
              {/* AC-DUC-023: 確定の失敗はモーダル内に出し、 実行ボタンで再試行させる。
                  確定し直せない失敗 (failureIsFinal) は、 失敗の内容だけを出す。 */}
              {failureView?.content}
              {!failureIsFinal && confirmDialog.renderBody(dialogState.result)}
              {/* AC-DUC-039: 結果を待つ間は画面を閉じないよう案内する。 */}
              {awaitsApplyResult && dialogState.phase === 'applying' && (
                <Text color="Text.midEmphasis" fontSize="sm">
                  {t('common:upload.status.applyingDoNotClose')}
                </Text>
              )}
            </Flex>
          }
          cancelLabel={resolveCancelLabel()}
          confirmLabel={confirmDialog.confirmLabel}
          hideConfirm={failureIsFinal}
          isLoading={dialogState.phase === 'applying'}
          isOpen
          onCancel={() =>
            dispatch({
              type: closeBacksToStep1 ? 'BACK_TO_STEP1' : 'CANCEL_CONFIRM',
            })
          }
          // 確定中の再押下は無視する (common.md §確定モーダル「二重 submit を
          // 防止」)。 ConfirmDialog の loading 表示だけに頼らず、 token 消費済みの
          // Apply を phase で止める。 確定し直せない失敗の後の確定も、 実行ボタンを
          // 隠すことだけに頼らず、 ここで止める。
          onConfirm={() => {
            if (dialogState.phase === 'applying' || failureIsFinal) return
            runApply(dialogState.result.checkToken)
          }}
          title={confirmDialog.title}
        />
      )}
    </Flex>
  )
}
